<template>
	<div :class="['navbar__item', {'has-dropdown' : isLoggedIn}]">

		<a role="button" tabindex="0" class="navbar__link" @click="navClick">{{ linkText }}</a>

		<!-- Dropdown -->
		<ul class="navbar__dropdown">
			<li class="navbar__dropdown-item">
				<a role="button" tabindex="0" class="navbar__dropdown-option" @click="submitLogout">Log Out</a>
			</li>
		</ul>

		<!-- Sidenav -->
		<portal selector="#sidebar__auth">
			<a v-if="!isLoggedIn" role="button" class="side-nav__link" @click="showForm('login')">Log In</a>
			<a
				v-if="!isLoggedIn"
				role="button"
				class="side-nav__link is-cta"
				@click="showForm('signup')"
			>Sign Up</a>
		</portal>

		<!-- Modal dialog box -->
		<FlytModal
			rootElement="form"
			:visible="isModalVisible"
			:title="form.title"
			:submit="form.submitText"
			:secondary="{
				text: form.secondaryText,
				payload: form.secondaryPayload
			}"
			@close="toggleModalVisible"
			@submit="delegateSubmit(form.submitHandler)"
			@secondary-action="showForm"
		>
			<LogInForm
				v-if="currentForm === 'login'"
				ref="login"
				:userNotFound="loginError"
				@forgot-password="showForm('forgot')"
			/>
			<ForgotPasswordForm
				v-if="currentForm === 'forgot'"
				ref="forgot"
				:status="forgotStatus"
				:message="forgotMessage"
			/>
			<SignUpForm v-if="currentForm === 'signup'" ref="signup" />
		</FlytModal>
	</div>
</template>

<script>
import FlytModal from "@/components/FlytModal"
import LogInForm from "@/components/forms/LogInForm"
import SignUpForm from "@/components/forms/SignUpForm"
import ForgotPasswordForm from "@/components/forms/ForgotPasswordForm2"
import { mapGetters, mapActions } from "vuex"
import { UserService } from "@/services/userService"

export default {
	data() {
		return {
			isUserMenuOpen: false,
			isModalVisible: false,
			loginError: false,
			forgotStatus: '',
			forgotMessage: '',
			currentForm: "login",
			configs: {
				loginForm: {
					title: "Login",
					submitText: "Login",
					submitHandler: "submitLogin",
					secondaryText: "New to FLYT? Create an account.",
					secondaryPayload: "signup",
				},
				signupForm: {
					title: "Welcome to FLYT!",
					submitText: "Sign Up",
					submitHandler: "submitSignup",
					secondaryText: "Already have an account? Log in.",
					secondaryPayload: "login",
				},
				forgotForm: {
					title: "Password Reset",
					submitText: "Send Reset Link",
					submitHandler: "submitForgot",
					secondaryText: "Back to login.",
					secondaryPayload: "login",
				},
			},
		}
	},

	props: {
		baseClass: {
			type: String,
		},
	},

	name: "NavigationAuth",

	components: { FlytModal, LogInForm, ForgotPasswordForm, SignUpForm },

	computed: {
		...mapGetters("auth", ["isLoggedIn", "user"]),

		linkText() {
			return this.isLoggedIn ? this.user.first_name : 'Login / Join'
		},

		form() {
			let formData
			switch (this.currentForm) {
				case "login":
					formData = this.configs.loginForm
					break
				case "signup":
					formData = this.configs.signupForm
					break
				case "forgot":
					formData = this.configs.forgotForm
					break
			}
			return formData
		},
	},

	methods: {
		...mapActions("auth", ["login", "logout", "register", "getUser"]),

		navClick() {
			if (this.isLoggedIn) this.toggleUserMenu()
			else this.showForm('login')
		},

		/**
		 * Switch modal visibility and, if transitioning to hidden,
		 * reset all form errors and default back to login form.
		 */
		toggleModalVisible() {
			this.isModalVisible = !this.isModalVisible
			if (!this.isModalVisible) {
				this.$nextTick(function () {
					this.loginError = false
					this.currentForm = "login"
				})
			}
		},

		/**
		 * Show modal, and optionally specify form
		 */
		showForm(form) {
			this.isModalVisible = true
			if (form) this.currentForm = form
		},

		/**
		 * Toggles user drop-down menu visibility
		 */
		toggleUserMenu() {
			this.isUserMenuOpen = !this.isUserMenuOpen
		},

		/**
		 * Run validation on current form,
		 * if valid, run the passed handler
		 */
		delegateSubmit(handler) {
			this.$refs[this.currentForm].$v.$touch()
			if (this.$refs[this.currentForm].$v.$invalid) return
			else this[handler]()
		},

		/**
		 * Run vuex register method
		 */
		submitSignup() {
			let payload = {
				first_name: this.$refs.signup.firstName,
				last_name: this.$refs.signup.lastName,
				email: this.$refs.signup.email,
				phone: this.$refs.signup.phone,
				password: this.$refs.signup.password,
			}
			this.register(payload).then((signInSuccessful) => {
				if (signInSuccessful) this.isModalVisible = false
			})
		},

		/**
		 * Run vuex submit method
		 */
		submitLogin() {
			let payload = {
				email: this.$refs.login.email,
				password: this.$refs.login.password,
			}
			this.login(payload)
				.then((isLoggedIn) => {
					if (!isLoggedIn)
						this.invalidLoginCredentials()
					else {
						this.$refs.login.email = ""
						this.$refs.login.password = ""
						this.isModalVisible = false
					}
				})
		},

		/*
		 * Send forgot password request
		 */
		submitForgot() {
			let payload = {
				email: this.$refs.forgot.email
			}
			UserService.forgotPassword(payload)
				.then((result) => {
					this.$refs.forgot.status = result.status
					this.$refs.forgot.message = result.message
				})
				.catch((error) => console.log("error :>> ", error))
		},

		/**
		 * Close user dropdown menu and run vuex logout method
		 */
		submitLogout() {
			this.isUserMenuOpen = false
			this.logout()
		},

		invalidLoginCredentials() {
			this.loginError = true
		},
	},

	mounted() {
		this.getUser()
	},
}
</script>

<style lang="scss" scoped>
.navbar__auth {
	display: flex;
	position: relative;
}

// Actual user anchor button
.navbar__user-cta {
	align-items: center;
	background: var(--color-background);
	color: #fff;
	display: flex;
	height: 100%;
	overflow: hidden;
	padding: 0 1rem;
	transition: background-color 300ms;
	width: 100%;
	// CTA underline
	&:after {
		background: var(--color-underline);
		bottom: 0;
		content: "";
		height: $navbar-track__height;
		left: 0;
		position: absolute;
		transition: background-color 300ms;
		width: 100%;
		z-index: 1;
	}
}
</style>